import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BsArrowRight } from "react-icons/bs";
import { IoAlertCircleSharp, IoEye, IoEyeOff } from "react-icons/io5";
import { HiOutlineShieldCheck, HiOutlineCloud } from "react-icons/hi";
import LoadingComponent from "../Components/LoadingComponent";
import useAuth from "../useAuth";
import logo from "../assets/logo.png";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const { user, authMessage, loading, loginWithEmailAndPassword } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    loginWithEmailAndPassword(email, password);
  };

  if (user) {
    return <Navigate to="/my/home" />;
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 overflow-hidden relative selection:bg-indigo-100 selection:text-indigo-900">
      {/* Dynamic Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute -top-24 -left-24 w-96 h-96 bg-indigo-200/40 rounded-full blur-[120px]"
        />
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            x: [0, -30, 0],
            y: [0, 50, 0],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
          className="absolute -bottom-24 -right-24 w-[30rem] h-[30rem] bg-blue-200/40 rounded-full blur-[130px]"
        />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.03)_0%,transparent_70%)]" />
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="w-full max-w-[1100px] flex flex-col md:flex-row gap-12 items-center relative z-10"
      >
        {/* Branding Section */}
        <div className="flex-1 text-center md:text-left space-y-8">
          {/* <motion.div variants={itemVariants} className="inline-flex items-center gap-3 px-4 py-2 bg-slate-900/5 backdrop-blur-md border border-slate-200 rounded-full mb-4">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            <span className="text-slate-600 text-[10px] font-bold uppercase tracking-[0.2em]">Enterprise Secure Cloud</span>
          </motion.div> */}

          <motion.div variants={itemVariants} className="space-y-4">
            <div className="relative inline-block mt-4">
              <div className="w-fit sm:h-32 mb-8 relative group">
                {/* Outer Glow */}
                <div className="absolute inset-0 " />

                {/* Gradient Border */}
                <div className="">
                  {/* Image Container */}
                  <div className="w-full h-full -ml-10 rounded-3xl flex items-center justify-center p-6 overflow-hidden">
                    <img
                      src={logo}
                      alt="Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* 
            <h1 className="text-5xl sm:text-7xl font-black text-slate-900 tracking-tighter leading-none">
              Drive | appdeploy <span className="bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">DRIVE</span>
            </h1> */}
            <p className="max-w-md text-slate-600 text-lg sm:text-xl font-medium leading-relaxed">
              Experience the next generation of cloud management. <span className="text-indigo-600">Secure. Fast. Minimal.</span>
            </p>
          </motion.div>


        </div>

        {/* Login Card */}
        <motion.div
          variants={itemVariants}
          className="w-full md:w-[460px] relative group"
        >
          <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500/10 to-blue-500/10 rounded-[3rem] blur-xl opacity-0 group-hover:opacity-100 transition duration-1000" />

          <form
            onSubmit={handleLogin}
            className="relative bg-white/80 backdrop-blur-3xl border border-white p-8 sm:p-12 rounded-[3rem] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.08)] space-y-8"
          >
            <div className="space-y-2">
              <h2 className="text-3xl font-black text-slate-900 tracking-tight">Access Drive</h2>
              <p className="text-slate-500 text-sm font-bold uppercase tracking-widest">Sign in with your workspace credentials</p>
            </div>

            <div className="space-y-6">
              {/* Username Input */}
              <div className="space-y-2 group/field">
                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-600 ml-1 group-focus-within/field:text-indigo-600 transition-colors">Username</label>
                <div className="relative">
                  <input
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="e.g. aditya_admin"
                    className="w-full bg-slate-50/50 border border-slate-200 rounded-2xl px-6 py-4.5 text-slate-900 font-bold outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/5 transition-all placeholder:text-slate-400"
                    required
                  />
                </div>
              </div>

              {/* Password Input */}
              <div className="space-y-2 group/field">
                <div className="flex justify-between items-center px-1">
                  <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-600 group-focus-within/field:text-indigo-600 transition-colors">Password</label>
                  <button type="button" className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-600 hover:text-indigo-500 transition-colors">Forgot?</button>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-slate-50/50 border border-slate-200 rounded-2xl px-6 py-4.5 text-slate-900 font-bold outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/5 transition-all placeholder:text-slate-400"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-indigo-600 transition-colors"
                  >
                    {showPassword ? <IoEyeOff size={20} /> : <IoEye size={20} />}
                  </button>
                </div>
              </div>
            </div>

            {/* Error Message */}
            <AnimatePresence mode="wait">
              {authMessage && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-3 px-5 py-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-xs font-bold leading-relaxed"
                >
                  <IoAlertCircleSharp size={20} className="shrink-0" />
                  <span>{authMessage?.slice(5).split("-").join(" ")}</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full relative group/btn h-16 overflow-hidden rounded-2xl bg-indigo-600 text-white font-black uppercase tracking-widest text-xs transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-600 group-hover/btn:translate-x-full transition-transform duration-700 ease-in-out" />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-600 -translate-x-full group-hover/btn:translate-x-0 transition-transform duration-700 ease-in-out" />
              <div className="relative flex items-center justify-center gap-3">
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    Sign In to Space
                    <BsArrowRight className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </div>
            </button>


          </form>
        </motion.div>
      </motion.div>


    </div>
  );
}
